import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const omni: Competitor = {
  "id": "omni",
  "name": "Omni Analytics",
  "group": "bi",
  "website": "https://omni.co",
  "owner": "Omni Analytics, Inc. (independent, venture-backed; Series C led by ICONIQ, April 2026)",
  "summary": "Browser-based BI platform built around a code-defined, git-versioned semantic model, with point-and-click, spreadsheet, SQL and AI ways of querying the warehouse. Founded by former Looker team members and aimed at data teams that want governed self-service.",
  "bestFor": [
    "Teams moving off Looker who want a modeling layer they can manage as code",
    "Analytics engineers who already use dbt and want two-way sync between dbt and the BI layer",
    "Companies that want AI answers and external AI clients (Claude, ChatGPT, Cursor via MCP) to work from the same governed metric definitions"
  ],
  "notFor": [
    "Organizations that need to self-host or run the BI tool on-prem (hosted service only)",
    "Buyers who want a published list price before talking to sales",
    "Teams without anyone to build and maintain a semantic model"
  ],
  "strengths": [
    "Git workflow for the model: branch mode, optional required pull requests, and support for GitHub, GitLab, Bitbucket and Azure DevOps. Omni's own comparison says Sigma offers only manual version tagging (vendor claim).",
    "Two-way dbt integration: can push descriptions and exposures to dbt and author dbt models from Omni, where Omni says Sigma mostly pulls metadata in (vendor claim).",
    "MCP server lets Claude, ChatGPT, Cursor, VS Code and Copilot query governed Omni data with the same permissions as in-app queries.",
    "One workbook can mix point-and-click queries, Excel-style spreadsheet tabs with cell references, and raw SQL tabs, so users can switch approach without rebuilding.",
    "AI features beyond Q&A: scheduled AI routines, saved skills, a modeling agent and evals."
  ],
  "limitations": [
    "No public pricing; quotes are by seat count through sales.",
    "Hosted only. Deployment docs describe instances and regions, not a self-hosted option.",
    "Data in spreadsheet tabs stays in that tab: it cannot feed other queries or be promoted to the shared model, and pivot tables or in-sheet charts are not supported.",
    "Input columns (writeback) are scoped to one workbook, cannot be promoted to the shared model, and need a scratch schema with database writebacks enabled.",
    "Python notebooks and embedded apps were only shown in demos or beta in Aug 2026; Omni says demos do not guarantee a release."
  ],
  "pricing": {
    "model": "per user (seat-based annual contract, quoted by sales)",
    "publicStartingPrice": "not published",
    "freeTier": "no free tier; free trial offered (length not stated)",
    "notes": "Omni has no pricing page. The AWS Marketplace listing quotes by user count on 12-, 24- and 36-month terms. Third-party buyer data from Vendr (not verified by Omni) puts the median annual contract at about $28k, with per-seat estimates around $50-100/user/month. AI usage is tracked in credits.",
    "sourceUrl": "https://aws.amazon.com/marketplace/pp/prodview-6ohhb7zzk5brq",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "live_warehouse_query": {
      "value": "yes",
      "note": "Queries run against the connected warehouse, with an optional results cache. SQL tabs run directly on the database.",
      "sourceUrl": "https://docs.omni.co/analyze-explore/sql/index.md"
    },
    "spreadsheet_interface": {
      "value": "yes",
      "note": "Spreadsheet tabs support Excel-style cell formulas and sheet references, and stay linked to live query results. Some syntax differs from Excel.",
      "sourceUrl": "https://docs.omni.co/analyze-explore/spreadsheet-tabs"
    },
    "sql_editor": {
      "value": "yes",
      "note": "Raw SQL tabs in the database dialect, which bypass the model.",
      "sourceUrl": "https://docs.omni.co/analyze-explore/sql/index.md"
    },
    "python_support": {
      "value": "unknown",
      "note": "An early Python notebook and the OmniFrames dataframe library were demoed in Aug 2026. No GA documentation found.",
      "sourceUrl": "https://docs.omni.co/demos/2026/20260821.md",
      "badge": "Demoed, not GA"
    },
    "writeback": {
      "value": "yes",
      "note": "Data input tables and editable input columns write to a scratch schema in the warehouse. Needs a scratch schema with writebacks enabled.",
      "sourceUrl": "https://docs.omni.co/analyze-explore/input-columns"
    },
    "semantic_layer": {
      "value": "yes",
      "note": "Shared semantic model with topics, plus a dbt semantic layer integration.",
      "sourceUrl": "https://docs.omni.co/_llms/docs.md"
    },
    "embedded_analytics": {
      "value": "yes",
      "note": "Signed-URL iframe embedding for external users, with user attributes driving row filtering. Embedded apps were in beta as of Aug 2026.",
      "sourceUrl": "https://docs.omni.co/embed/index.md"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Omni Agent chat, workbook agent, chat with dashboards, routines, skills and an MCP server. AI is metered in credits.",
      "sourceUrl": "https://docs.omni.co/ai/index.md"
    },
    "version_control_git": {
      "value": "yes",
      "note": "Model-level git sync with GitHub, GitLab, Bitbucket or Azure DevOps. Requires branch mode; pull requests can be enforced.",
      "sourceUrl": "https://docs.omni.co/integrations/git"
    },
    "open_source_or_self_host": {
      "value": "no",
      "note": "Hosted service only (region selectable). Absence of self-hosting stated by a competitor's comparison page; Omni's deployment guide lists only hosted instance patterns.",
      "sourceUrl": "https://docs.omni.co/guides/deployment/choosing-your-deployment"
    },
    "desktop_authoring_required": {
      "value": "no",
      "note": "Sold as SaaS and authored in the browser.",
      "sourceUrl": "https://aws.amazon.com/marketplace/pp/prodview-6ohhb7zzk5brq"
    },
    "scheduled_delivery": {
      "value": "yes",
      "note": "Schedules and conditional alerts to email, Slack, Google Sheets, S3, SFTP or a webhook.",
      "sourceUrl": "https://docs.omni.co/share/deliveries/index.md"
    },
    "row_level_security": {
      "value": "yes",
      "note": "Access filters limit rows by user attribute.",
      "sourceUrl": "https://docs.omni.co/modeling/topics/parameters/access-filters"
    },
    "excel_export": {
      "value": "yes",
      "note": "Dashboards download as XLSX, CSV (zipped per tile), PDF or PNG.",
      "sourceUrl": "https://docs.omni.co/visualize-present/dashboards/download.md"
    }
  },
  "recentChanges": [
    {
      "date": "2025-10",
      "change": "Acquired embedded-analytics vendor Explo. The Explo platform will run for 12 months while customers migrate to Omni.",
      "sourceUrl": "https://omni.co/blog/omni-accelerates-growth-with-acquisition-of-explo"
    },
    {
      "date": "2026-04",
      "change": "Raised a $120M Series C at a $1.5B valuation, led by ICONIQ. Omni reports 4x year-over-year revenue growth.",
      "sourceUrl": "https://omni.co/blog/press-release-omni-series-c-funding"
    },
    {
      "date": "2026-04",
      "change": "Positioned the semantic model as context for AI, including MCP access from Claude, ChatGPT, Cursor and VS Code.",
      "sourceUrl": "https://omni.co/blog/press-release-omni-series-c-funding"
    },
    {
      "date": "2026-08",
      "change": "Demoed an early Python notebook (OmniFrames), write-back apps, a Metabase-to-Omni migration tool, and embedded apps in beta.",
      "sourceUrl": "https://docs.omni.co/demos/2026/20260821.md"
    }
  ],
  "sources": [
    {
      "title": "Omni Series C press release",
      "url": "https://omni.co/blog/press-release-omni-series-c-funding"
    },
    {
      "title": "Omni acquires Explo",
      "url": "https://omni.co/blog/omni-accelerates-growth-with-acquisition-of-explo"
    },
    {
      "title": "Omni vs Sigma (vendor-authored)",
      "url": "https://omni.co/omni-vs-sigma"
    },
    {
      "title": "Omni docs: AI",
      "url": "https://docs.omni.co/ai/index.md"
    },
    {
      "title": "Omni docs: Git integration",
      "url": "https://docs.omni.co/integrations/git"
    },
    {
      "title": "Omni docs: Spreadsheet tabs",
      "url": "https://docs.omni.co/analyze-explore/spreadsheet-tabs"
    },
    {
      "title": "Omni docs: Input columns",
      "url": "https://docs.omni.co/analyze-explore/input-columns"
    },
    {
      "title": "Omni docs: Embedding",
      "url": "https://docs.omni.co/embed/index.md"
    },
    {
      "title": "Omni docs: Access filters",
      "url": "https://docs.omni.co/modeling/topics/parameters/access-filters"
    },
    {
      "title": "Omni docs: Deliveries",
      "url": "https://docs.omni.co/share/deliveries/index.md"
    },
    {
      "title": "Omni docs: Dashboard downloads",
      "url": "https://docs.omni.co/visualize-present/dashboards/download.md"
    },
    {
      "title": "Omni demo notes, Aug 21 2026",
      "url": "https://docs.omni.co/demos/2026/20260821.md"
    },
    {
      "title": "AWS Marketplace listing (pricing terms)",
      "url": "https://aws.amazon.com/marketplace/pp/prodview-6ohhb7zzk5brq"
    },
    {
      "title": "Vendr buyer pricing data (third party)",
      "url": "https://www.vendr.com/marketplace/omni-analytics"
    },
    {
      "title": "Metabase vs Omni (competitor page, third party)",
      "url": "https://www.metabase.com/lp/metabase-vs-omni"
    }
  ],
  "lastReviewed": "2026-11-03"
};
